import amplitude from 'amplitude-js';
import { createVisit } from 'app/core/http';
import { PageType } from 'app/core/page';
import { SurveyConfig } from 'app/core/survey-config';
import { createAmplitudeEventData } from './createAmplitudeEventData';

/**
 * The service for managing all tracking code methods.
 * Support `ga`, `fbpixel`, `amplitude`.
 */
export class Tracker {
  private _officialGaInitialed = false;
  private _officialAmplitudeInitialed = false;
  private _userGaInitialed = false;
  private _userFbpixelInitialed = false;

  constructor(
    private _surveyConfig: SurveyConfig,
    private _gaId?: string,
    private _amplitudeKey?: string
  ) {}

  /**
   * If `true`, all tracking methods will be disalbed.
   */
  private _isDisabled() {
    const { query } = this._surveyConfig;
    return Boolean(query && (query.status === 'preview' || query.untrack === 1));
  }

  private _createVisit() {
    createVisit(this._surveyConfig.surveyHash)
  }

  private _initialOfficialGa() {
    if (this._gaId) {
      ga('create', this._gaId, 'auto');
      this._officialGaInitialed = true;
    }
  }

  private _initialOfficialAmplitude() {
    if (this._amplitudeKey) {
      amplitude.getInstance().init(this._amplitudeKey);
      amplitude.getInstance().setUserId('');
      this._officialAmplitudeInitialed = true;
    }
  }

  private _initialUserGa() {
    const { analyticsid } = this._surveyConfig.data;

    if (analyticsid) {
      ga('create', analyticsid, 'auto', 'clientTracker');
      this._userGaInitialed = true;
    }
  }

  private _initialUserFbpixel() {
    const { fbpixelid } = this._surveyConfig.data;

    if (fbpixelid) {
      fbq('init', fbpixelid);
      this._userFbpixelInitialed = true;
    }
  }

  /**
   * While the survey opened no matter if loaded.
   */
  trackEnterSurvey() {
    this._initialOfficialGa();
    this._initialOfficialAmplitude();

    if (this._officialGaInitialed) {
      ga('send', 'pageview');
    }
  }

  /**
   * While the survey has been loaded and the loading mask has been removed.
   */
  trackSurveyRendered(page: PageType) {
    if (this._isDisabled()) {
      return;
    }

    this._createVisit();
    this._initialUserGa();
    this._initialUserFbpixel();

    if (this._officialAmplitudeInitialed) {
      amplitude
        .getInstance()
        .logEvent(
          'Enter Survey',
          createAmplitudeEventData(
            this._surveyConfig.surveyHash,
            this._surveyConfig.query,
            page
          )
        );
    }

    if(this._officialGaInitialed) {
      ga(
        'send',
        'event',
        'SurveyCakeVisit',
        this._surveyConfig.surveyHash
      );
    }

    if (this._userGaInitialed) {
      ga('clientTracker.send', 'pageview');
      ga(
        'clientTracker.send',
        'event',
        'SurveyCake',
        'SurveyCakeVisit',
        this._surveyConfig.surveyHash
      );
    }

    if (this._userFbpixelInitialed) {
      fbq('trackCustom', 'PageView');
      //	@ts-ignore
      fbq('trackCustom', 'SurveyCakeVisit', {
        //	@ts-ignore
        surveyID: this._surveyConfig.surveyHash
      });
    }
  }

  /**
   * After the survey submitted successfully.
   */
  trackSubmitSuccess(page: PageType) {
    if (this._isDisabled()) {
      return;
    }

    if (this._officialGaInitialed) {
      ga('send', 'event', 'SubmitSuccess', this._surveyConfig.surveyHash);
    }

    this._createVisit();

    if (this._officialAmplitudeInitialed) {
      amplitude
        .getInstance()
        .logEvent(
          'Submit Survey',
          createAmplitudeEventData(
            this._surveyConfig.surveyHash,
            this._surveyConfig.query,
            page
          )
        );
    }

    if (this._userGaInitialed) {
      ga(
        'clientTracker.send',
        'event',
        'SurveyCake',
        'SurveyCakeSubmit',
        this._surveyConfig.surveyHash
      );
    }

    if (this._userFbpixelInitialed) {
      //	@ts-ignore
      fbq('trackCustom', 'SurveyCakeSubmit', {
        //	@ts-ignore
        surveyID: this._surveyConfig.surveyHash
      });
    }
  }
}
